import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import _ from 'lodash';

import { IContextualMenuItem, ContextualMenuItemType, MessageBar, MessageBarType, IColumn } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';

import { RootState } from '../../../../../redux/application/redux-store';
import { clearCriteria } from '../../../../../redux/slices/Ticket/ticketSlice';
import { Ticket, TicketFilteringCriteria } from '../../../../../api-client';
import { RouterSettings } from '../../../../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useTicketsPivot } from './hooks/useTicketsPivot';

import {
    IAIDetailsList,
    IAIContextualMenuColumn,
    IAIDatetimeColumn,
    IAIPreviewColumn,
    IAIPriorityColumn,
    IAIStatusColumn,
    IAIStringColumn
} from '../../../../../common/components/IAITable';
import { IAITicketsRefinementDrawer } from '../../../../../common/components/IAITicketsRefinementDrawer';
import ChangeLogsDrawer from './components/ChangeLogsDrawer/change-logs-drawer';
import DeleteDialog from './components/DeleteDialog/delete-dialog';
import IAIColumnsRefinementDrawer from '../../../../../common/components/IAIColumnsRefinementDrawer/iai-columns-refinements-drawer';
import { ITicketColumn } from '../../../../../common/components/IAITable/IAIDetailsList/iai-details-list';
import IAISolvedStatusColumn from '../../../../../common/components/IAITable/IAISolvedStatusColumn/iai-solved-status-column';

interface ITicketsPivot {
    dataSource: Ticket[];
    dataLoading?: boolean;
    onApplyCriteria: (filterCriteria: TicketFilteringCriteria | undefined) => void;
    onClearCriteria: () => void;
    deleteTicketLoading?: boolean;
    onDeleteTicket: (id: string, onSuccess: () => void) => void;
    onExportTicket?: () => void;
}

const TicketsPivot: React.FunctionComponent<ITicketsPivot> = ({
    dataSource,
    dataLoading,
    onApplyCriteria,
    onClearCriteria,
    deleteTicketLoading,
    onDeleteTicket,
    onExportTicket
}) => {
    const {
        handleRetrieveChangeLogs,
        retrieveChangeLogs,
        retrieveChangeLogsLoading
    } = useTicketsPivot();

    const _handleSearchPredicate = (dataItem: Ticket, searchKey: string) => (
        _.includes(dataItem.id?.toLowerCase(), searchKey) ||
        _.includes(dataItem.subject?.toLowerCase(), searchKey) ||
        _.includes(dataItem.description?.toLowerCase(), searchKey) ||
        _.includes(dataItem.requestedBy?.toLowerCase(), searchKey)
    );

    //#region Form-Management

    const [changeLogsDrawerVisible, { toggle: toggleChangeLogsDrawerVisible }] = useBoolean(false);
    const [deleteTicketModalVisible, { toggle: toggleDeleteTicketModalVisible }] = useBoolean(false);
    const [delegateTicket, setDelegateTicket] = useState<Ticket>();

    const navigate = useNavigate();

    const _handleCreateAction = () => navigate(RouterSettings.PATHS.dashboard.ticket.edit('00-0000'));

    const _handleDeleteAction = (dataItem: Ticket) => {
        setDelegateTicket(dataItem);
        toggleDeleteTicketModalVisible();
    }

    const _handleDismissDeleteAction = () => {
        setDelegateTicket(undefined);
        toggleDeleteTicketModalVisible();
    }

    const _handleRetrieveChangeLogs = (id: string) => {
        handleRetrieveChangeLogs({
            id: id,
            onRetrieveSuccess: (changeLogs) => toggleChangeLogsDrawerVisible()
        });
    }

    const _handleDeleteTicket = () => {
        if (delegateTicket?.id) {
            onDeleteTicket(delegateTicket.id, _handleDismissDeleteAction);
        }
    }

    //#endregion

    //#region Criteria-Management

    const iaiCriteria = useSelector((state: RootState) => state.ticket.criteria);
    const properties = useSelector((state: RootState) => state.system.customAttributes);

    const [refinementDrawerVisible, { toggle: toggleRefinementDrawerVisible }] = useBoolean(false);
    const [columnsRefinementDrawerVisibile, { toggle: toggleColumnsRefinementDrawerVisibile }] = useBoolean(false);

    const dispatch = useDispatch();

    const _handleClearTicketsCriteria = () => {
        dispatch(clearCriteria());
        onClearCriteria();
        toggleRefinementDrawerVisible();
    }
    //#endregion

    const {
        t: translate
    } = useTranslation();

    const iaiAccount = useSelector((state: RootState) => state.account);
    const [activeColumns, setActiveColumns] = useState<ITicketColumn[]>([]);

    const _handleMenuItemsByRole = (dataItem: Ticket, dataIndex?: number): IContextualMenuItem[] => {
        let menuItems: IContextualMenuItem[] = [
            {
                key: `menuItem__edit_${dataIndex}`,
                iconProps: { iconName: 'EditCreate' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandEditLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandEditTooltip),
                onClick: () => navigate(RouterSettings.PATHS.dashboard.ticket.edit(dataItem.id!))
            }
        ];

        if (iaiAccount.role === 'administrator') {
            menuItems.push({
                key: `menuItem__changeLogs_${dataIndex}`,
                iconProps: { iconName: 'ViewAll' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandChangeLogsLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandChangeLogsTooltip),
                onClick: () => _handleRetrieveChangeLogs(dataItem.id!)
            });
        }

        menuItems.push({
            key: `menuItem__divider_${dataIndex}`,
            itemType: ContextualMenuItemType.Divider
        });

        if (dataItem.status != 'deleted' &&
            (iaiAccount.role === 'administrator' || iaiAccount.role === 'departmentManager')) {
            menuItems.push({
                key: `menuItem__delete_${dataIndex}`,
                iconProps: { iconName: 'Delete' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteTooltip),
                onClick: () => _handleDeleteAction(dataItem)
            });
        }

        return menuItems;
    }

    const propertiesColumns = () => {
        return _.map(properties, dataItem => ({
            key: `tck__Property_${dataItem.id}`,
            name: dataItem.displayName,
            minWidth: 100,
            isResizable: true,
            onRender: (dataSourceItem: Ticket) => (
                <IAIStringColumn align="start" text={dataSourceItem?.properties?.find(x => x.id === dataItem.id)?.value} />
            ),
            isActive: dataItem?.defaultRender,
        } as ITicketColumn))
    };

    const dataColumns = useMemo(() => {
        return [
            {
                key: 'tck__preview',
                name: '',
                minWidth: 25,
                maxWidth: 25,
                onRender: (dataItem: Ticket) => (
                    <IAIPreviewColumn
                        heading={dataItem.subject}
                        content={parse(dataItem.description || '')}
                        tooltip={translate(TRANSLATION_KEYS.ColumnPreviewTooltip)}
                    />
                ),
                isLocked: true,
            },
            {
                key: 'tck__subject',
                name: translate(TRANSLATION_KEYS.ColumnTitleLabel),
                minWidth: 150,
                isResizable: true,
                isMultiline: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.subject} />
                ),
                isActive: true,
            },
            {
                key: 'tck__createdAt',
                name: translate(TRANSLATION_KEYS.ColumnCreatedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.createdAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__requestedBy',
                name: translate(TRANSLATION_KEYS.ColumnRequestedByLabel),
                minWidth: 100,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.requestedBy} />
                ),
                isActive: true,
            },
            {
                key: 'tck__status',
                name: translate(TRANSLATION_KEYS.ColumnStatusLabel),
                minWidth: 75,
                maxWidth: 75,
                onRender: (dataItem: Ticket) => (
                    <IAIStatusColumn align="start" status={dataItem.status} />
                ),
                isActive: true,
            },
            {
                key: 'tck__department',
                name: translate(TRANSLATION_KEYS.ColumnDepartmentLabel),
                minWidth: 100,
                isResizable: true,
                isMultiline: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.department?.displayName} />
                ),
                isActive: true,
            },
            {
                key: 'tck__agent',
                name: translate(TRANSLATION_KEYS.ColumnAgentLabel),
                minWidth: 100,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.agent?.displayName} />
                ),
                isActive: true,
            },
            {
                key: 'tck__priority',
                name: translate(TRANSLATION_KEYS.ColumnPriorityLabel),
                minWidth: 75,
                maxWidth: 75,
                onRender: (dataItem: Ticket) => (
                    <IAIPriorityColumn align="start" priority={dataItem.priority} />
                ),
                isActive: true,
            },
            {
                key: 'tck__solvedInDays',
                name: translate(TRANSLATION_KEYS.ColumnSolvedInDaysLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.status === 'closed' ? `${dataItem.solvedInDays || 0} ${translate(TRANSLATION_KEYS.RowDaysLabel)}` : ""} />
                ),
                isActive: true,
            },
            {
                key: 'tck__solvedStatus',
                name: translate(TRANSLATION_KEYS.ColumnSolvedStatusLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAISolvedStatusColumn align="start" ticketStatus={dataItem.status} solvedInDays={dataItem.solvedInDays} departmentId={dataItem.department?.id} />
                ),
                isActive: true,
            },
            {
                key: 'tck__closedAt',
                name: translate(TRANSLATION_KEYS.ColumnClosedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.closedAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__canceledAt',
                name: translate(TRANSLATION_KEYS.ColumnCanceledAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.canceledAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__deletedAt',
                name: translate(TRANSLATION_KEYS.ColumnDeletedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.deletedAt} />
                ),
                isActive: true,
            },
            ...propertiesColumns(),
            {
                key: 'tck__actions',
                name: '',
                minWidth: 25,
                maxWidth: 25,
                onRender: (dataItem: Ticket, dataIndex: any) => (
                    <IAIContextualMenuColumn
                        menuItems={_handleMenuItemsByRole(dataItem, dataIndex)}
                    />
                ),
                isLocked: true,
            }
        ]
    }, [properties]);

    useEffect(() => {
        setActiveColumns(dataColumns);
    }, [properties]);

    return (
        <div className={`tickets-pivot-container`}>
            {!!Object.keys(iaiCriteria || {}).length && (
                <MessageBar
                    messageBarType={MessageBarType.info}
                    styles={{ root: { marginTop: 15, marginBottom: 5 } }}
                >
                    {translate(TRANSLATION_KEYS.ActiveCriteriaMessage)}
                </MessageBar>
            )}

            <IAIDetailsList
                dataSource={dataSource}
                dataColumns={activeColumns.filter(x => x?.isActive || x?.isLocked)}
                dataLoading={dataLoading}
                commands={[
                    {
                        key: '',
                        disabled: dataLoading,
                        text: translate(TRANSLATION_KEYS.CommandCreateLabel),
                        title: translate(TRANSLATION_KEYS.CommandCreateTooltip),
                        iconProps: { iconName: 'Add' },
                        onClick: _handleCreateAction
                    }
                ]}
                sideCommands={[
                    {
                        key: 'column',
                        disabled: dataLoading,
                        title: translate(TRANSLATION_KEYS.CommandFilterTooltip),
                        iconProps: { iconName: 'ColumnOptions' },
                        onClick: toggleColumnsRefinementDrawerVisibile
                    },
                    {
                        key: 'filter',
                        disabled: dataLoading,
                        title: translate(TRANSLATION_KEYS.CommandFilterTooltip),
                        iconProps: { iconName: 'Filter' },
                        onClick: toggleRefinementDrawerVisible
                    }
                ]}
                emptyContentLabel={translate(TRANSLATION_KEYS.DetailsListEmptyContentLabel)}
                onItemInvoked={(dataItem: Ticket) => navigate(RouterSettings.PATHS.dashboard.ticket.edit(dataItem.id!))}
                onSearchPredicate={_handleSearchPredicate}
                exportCommands={onExportTicket && [
                    {
                        key: '',
                        disabled: dataLoading,
                        text: translate(TRANSLATION_KEYS.CommandExportLabel),
                        title: translate(TRANSLATION_KEYS.CommandExportTooltip),
                        iconProps: { iconName: 'ExcelDocument' },
                        onClick: onExportTicket
                    }
                ]}
            />

            <IAITicketsRefinementDrawer
                criteria={iaiCriteria}
                disabled={dataLoading}
                drawerVisible={refinementDrawerVisible}
                onApplyCriteria={(filterCriteria) => {
                    onApplyCriteria(filterCriteria);
                    toggleRefinementDrawerVisible();
                }}
                onClearCriteria={_handleClearTicketsCriteria}
                onDrawerDismiss={toggleRefinementDrawerVisible}
            />

            <IAIColumnsRefinementDrawer
                disabled={dataLoading}
                drawerVisible={columnsRefinementDrawerVisibile}
                columns={dataColumns}
                onApplyCriteria={(selectedColumns) => {
                    setActiveColumns(selectedColumns);
                    toggleColumnsRefinementDrawerVisibile();
                }}
                onDrawerDismiss={toggleColumnsRefinementDrawerVisibile}
            />

            <ChangeLogsDrawer
                changeLogs={retrieveChangeLogs}
                disabled={dataLoading || retrieveChangeLogsLoading}
                drawerVisible={changeLogsDrawerVisible}
                onDrawerDismiss={toggleChangeLogsDrawerVisible}
            />

            <DeleteDialog
                disabled={dataLoading || deleteTicketLoading}
                modalVisible={deleteTicketModalVisible}
                onModalConfirm={_handleDeleteTicket}
                onModalDismiss={_handleDismissDeleteAction}
            />
        </div>
    );
}

export default TicketsPivot;